import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Box, Select, MenuItem, FormControl, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import OrderingList from "../Lists/OrderingList";
import { shuffle } from "lodash";

const useStyles = makeStyles(theme => ({
  matchingText: {
    ...theme.typography.subtitle1
  }
}));

// TODO: implement actual functionality to update user answer based on selected match in separate ticket
const MatchSelectField = ({ value, matchChoices, onChange, disabled }) => {
  const menuItems = matchChoices
    ? matchChoices.map((matchChoice, index) => ({
        label: index + 1,
        value: matchChoice.id
      }))
    : [];
  return (
    <Box width={80}>
      <FormControl hiddenLabel fullWidth>
        <Select
          variant="filled"
          onChange={({ target: { value } }) => onChange(value)}
          value={value}
          disabled={disabled}
          data-qa="match-question-select-field"
        >
          {menuItems.map((menuItem, index) => (
            <MenuItem value={menuItem.value} key={index}>
              {menuItem.label}
            </MenuItem>
          ))}
          <MenuItem value="" style={{ display: "none" }} />
        </Select>
      </FormControl>
    </Box>
  );
};

const MatchingQuestion = ({
  matchChoices,
  matchClues,
  existingAnswers,
  onChange,
  disabled
}) => {
  const classes = useStyles();
  const [matchChoiceList, setMatchChoiceList] = useState([]);
  // An object consisting of key value pairs where the key is the clue ID and the value is the choice ID
  const [currentMatches, setCurrentMatches] = useState(null);

  useEffect(() => {
    if (matchChoices) {
      setMatchChoiceList(shuffle(matchChoices));
    }
  }, [matchChoices]);

  useEffect(() => {
    /**
     * Creates the key value pairs with any existing answers. If the match clue,
     * does not have an existing match choice yet, we set its value to an empty string
     */
    if (matchClues) {
      const matchList = matchClues.reduce((current, matchClue) => {
        const existingMatch = existingAnswers
          ? existingAnswers.find(
              answer =>
                answer &&
                answer.matchClue &&
                answer.matchClue.id === matchClue.id
            )
          : null;
        return {
          ...current,
          [matchClue.id]:
            existingMatch && existingMatch.matchChoice
              ? existingMatch.matchChoice.id
              : ""
        };
      }, {});
      setCurrentMatches(matchList);
    }
  }, [matchClues, existingAnswers]);

  const handleMatchChange = matchClueId => matchChoiceId => {
    let newMatches = { ...currentMatches };
    setCurrentMatches({
      ...newMatches,
      [matchClueId]: matchChoiceId
    });
    onChange({ matchClueId, matchChoiceId });
  };

  return (
    <Box display="flex" flex={1} mt={5} mb={2} ml={3.75}>
      <Box flex={1}>
        <OrderingList
          listItems={matchClues}
          renderText={matchingItem => (
            <Box
              display="flex"
              alignItems="center"
              dangerouslySetInnerHTML={{ __html: matchingItem.text }}
              className={classes.matchingText}
            />
          )}
          renderOrderField={(matchClue, index) =>
            currentMatches ? (
              <MatchSelectField
                matchChoices={matchChoiceList}
                value={currentMatches[matchClue.id]}
                onChange={handleMatchChange(matchClue.id)}
                disabled={disabled}
              />
            ) : null
          }
        />
      </Box>
      <Box flex={1}>
        <OrderingList
          listItems={matchChoiceList}
          renderText={matchingItem => (
            <Box
              display="flex"
              alignItems="center"
              dangerouslySetInnerHTML={{ __html: matchingItem.text }}
              className={classes.matchingText}
            />
          )}
          renderOrderField={(matchClue, index) => (
            <Typography variant="subtitle1">{`${index + 1}.`}</Typography>
          )}
        />
      </Box>
    </Box>
  );
};

MatchingQuestion.propTypes = {
  matchClues: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The match clue text
       */
      text: PropTypes.string,
      position: PropTypes.number,
      id: PropTypes.string,
      type: PropTypes.string
    })
  ),
  matchChoices: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The match clue text
       */
      text: PropTypes.string,
      position: PropTypes.number,
      id: PropTypes.string,
      type: PropTypes.string
    })
  ),
  onChange: PropTypes.func,
  existingAnswers: PropTypes.arrayOf(
    PropTypes.shape({
      matchChoice: PropTypes.shape({
        /**
         * The match choice text
         */
        text: PropTypes.string,
        position: PropTypes.number,
        id: PropTypes.string,
        type: PropTypes.string
      }),
      matchClue: PropTypes.shape({
        /**
         * The match clue text
         */
        text: PropTypes.string,
        position: PropTypes.number,
        id: PropTypes.string,
        type: PropTypes.string
      })
    })
  )
};

export default MatchingQuestion;
